// ---------------------------------------------------------------------------
// validateOrders — is this round's plan one a player could legally submit?
// ---------------------------------------------------------------------------
//
// The rules the app used to enforce only in its UI and hook, as one pure
// check. A plan is what submitOrders takes: the planning board (the
// start-of-round board with this round's moves, deploys, brood builds and
// attacks applied through the gameLogic order functions), plus targets, drop
// zones, rockets and recon planes. It is checked against the player as the
// last resolution left them (start-of-round board, Grid, yield):
//
//   units      nothing appears or vanishes: the round's veterans, movers and
//              brood pairs account for every drone that started the round;
//              airdropped drones stay put; no corridor over capacity
//   deploys    exactly armiesPerRound fresh drones — fewer only when no
//              corridor can take another
//   moves      one out per corridor at most; each drone that moved in came
//              from a neighbour whose move is spent
//   broods     each on a spot canPlaceBase accepts, clear of the corridor's
//              other pending broods. (The board refuses a BUILD in a corridor
//              already attacking, but lets it attack after building — so a
//              plan with both is legal: that gate is about the order of taps.)
//   attacks    only from a corridor canAttack allows (none before the war's
//              firstAttackRound); each aimed at a subplot
//              inside that corridor (plot row matching); no target elsewhere
//   drop zones exactly min(owed, open slots), each placeable in turn
//   rockets    on a live cell of a standing structure; one per
//              cell, never on a cell launching a plane
//   planes     the same cells; one per launch cell; each aimed inside its
//              own corridor
//   spend      rockets + planes within the player's yield
//
// Returns { ok, errors: [string, ...] }.

import { BATTLEFIELD_COUNT } from './gameConfig.js'
import { rules, withRules } from './config.js'
import {
    ensureSlots, basesOf, canAttack, deployArmy, requiredDropZones, canPlaceDropZone, movedFromOf,
} from './gameLogic.js'
import { canPlaceBase, baseFootprint } from './subplotBases.js'
import { isStructureDestroyed, battlefieldColumn, plotOf, migrateBroods, hasLegacyBroods } from './subplotGrid.js'

const parseHf = (raw) => {
    if (raw == null || raw === '') return null
    if (typeof raw === 'string') return migrateBroods(JSON.parse(raw))
    return hasLegacyBroods(raw) ? migrateBroods(JSON.parse(JSON.stringify(raw))) : raw
}
const units = (bf) => (bf.armyQty || 0) + (bf.newArmies || 0) + (bf.movedInArmies || 0)
const openSlots = (board) => board.reduce((n, bf) => n + Math.max(0, bf.maxArmyQty - units(bf)), 0)

// Cells a rocket may sit on or a plane launch from.
export function assetCellOk(hf, s) {
    const cell = hf?.subplots?.[s]
    if (!cell || cell.destroyed || cell.structureId == null) return false
    const st = hf.structures?.[cell.structureId]
    return !!st && !isStructureDestroyed(hf, cell.structureId)
}

export function validateOrders(player, orders, { config = null } = {}) {
    return withRules(config, () => check(player, orders))
}

function check(player, orders) {
    const errors = []
    const err = (m) => errors.push(m)
    const start = (player?.board ?? []).map(ensureSlots)
    const plan = (orders?.board ?? []).map(ensureSlots)
    const hf = parseHf(player?.homefront)
    if (plan.length !== start.length || !plan.length) return { ok: false, errors: ['The plan has no board for this war.'] }

    // ── Units ──────────────────────────────────────────────────────────────
    let startDrones = 0, planDrones = 0, pendingPairs = 0, fresh = 0
    plan.forEach((bf, col) => {
        const s = start[col]
        startDrones += (s.armyQty || 0)
        planDrones += (bf.armyQty || 0) + (bf.movedInArmies || 0)
        pendingPairs += (bf.pendingBaseBuilds ?? []).length
        if ((bf.deliveredNewArmies || 0) !== (s.deliveredNewArmies || 0) || (bf.newArmies || 0) < (s.newArmies || 0)) {
            err(`Corridor ${col + 1}: airdropped drones can't be moved or removed.`)
        }
        fresh += (bf.newArmies || 0) - (s.newArmies || 0)
        if (units(bf) > bf.maxArmyQty) err(`Corridor ${col + 1} is over capacity.`)
    })
    if (planDrones + rules().broodCost * pendingPairs !== startDrones) err('Drones appeared or vanished: the plan does not account for every drone that started the round.')

    // ── Deploys ────────────────────────────────────────────────────────────
    const owed = rules().armiesPerRound
    if (fresh > owed) err(`Only ${owed} drones can be deployed a round (the plan deploys ${fresh}).`)
    if (fresh < owed) {
        const couldDeploy = plan.some(bf => deployArmy(plan, bf.id, owed - fresh).success)
        if (couldDeploy) err(`Deploy all ${owed} drones before submitting (${owed - fresh} left).`)
    }

    // ── Moves ──────────────────────────────────────────────────────────────
    const movedOut = plan.filter(bf => bf.movedOut).length
    const movedIn = plan.reduce((n, bf) => n + (bf.movedInArmies || 0), 0)
    if (movedOut !== movedIn) err('Every move needs a corridor it left and one it arrived in.')
    plan.forEach((bf, col) => {
        for (const t of bf.slots) {
            if (t?.kind !== 'moved') continue
            const side = movedFromOf(bf, t)
            const src = side === 'left' ? col - 1 : side === 'right' ? col + 1 : -1
            if (src < 0 || src >= plan.length || !plan[src].movedOut) err(`Corridor ${col + 1}: a drone arrived from a corridor that didn't move.`)
        }
    })

    // ── Broods ─────────────────────────────────────────────────────────────
    plan.forEach((bf, col) => {
        const pending = bf.pendingBaseBuilds ?? []
        if (!pending.length) return
        const taken = new Set()
        for (const p of pending) {
            const fp = baseFootprint(p.centerS) ?? []
            if (!hf || !canPlaceBase(hf, col, p.centerS)) err(`Corridor ${col + 1}: a brood can't stand at subplot ${p.centerS}.`)
            if (fp.some(s => taken.has(s))) err(`Corridor ${col + 1}: two broods overlap.`)
            fp.forEach(s => taken.add(s))
            if (!basesOf(bf).some(b => b.centerS === p.centerS)) err(`Corridor ${col + 1}: a pending brood is missing from the corridor's ledger.`)
        }
    })

    // ── Attacks and targets ────────────────────────────────────────────────
    const targetPlots = orders.targetPlots ?? Array(BATTLEFIELD_COUNT).fill(null)
    const targetSubplots = orders.targetSubplots ?? Array(BATTLEFIELD_COUNT).fill(null)
    plan.forEach((bf, col) => {
        // The plan comes from the client: the wait is the war's, not the plan's.
        if ((bf.attackHold ?? 0) !== (start[col].attackHold ?? 0)) err(`Corridor ${col + 1}'s attack wait doesn't match the war.`)
        const sub = targetSubplots[col] ?? null
        const row = targetPlots[col] ?? null
        if (bf.isAttacking) {
            const v = canAttack(bf)
            if (!v.valid) err(`Corridor ${col + 1}: ${v.reason}`)
            if (sub == null || row == null) err(`Corridor ${col + 1} attacks but has no target.`)
            else if (battlefieldColumn(sub) !== col) err(`Corridor ${col + 1}'s target is outside its corridor.`)
            else if (plotOf(sub).row !== row) err(`Corridor ${col + 1}'s target plot doesn't match its subplot.`)
        } else if (sub != null || row != null) {
            err(`Corridor ${col + 1} has a target but isn't attacking.`)
        }
    })

    // ── Drop zones ─────────────────────────────────────────────────────────
    const zones = orders.dropZones ?? []
    const owedZones = Math.min(requiredDropZones(plan), openSlots(plan))
    if (zones.length !== owedZones) err(`Place exactly ${owedZones} drop zone${owedZones === 1 ? '' : 's'} (the plan has ${zones.length}).`)
    zones.forEach((col, i) => {
        const v = canPlaceDropZone(plan, col, zones.slice(0, i))
        if (!v.valid) err(`Drop zone ${i + 1}: ${v.reason}`)
    })

    // ── Rockets and recon planes ───────────────────────────────────────────
    const rockets = orders.rockets ?? []
    const drones = orders.drones ?? []
    const used = new Set()
    for (const s of rockets) {
        if (!assetCellOk(hf, s)) { err(`A rocket can't go on subplot ${s}.`); continue }
        const st = hf.structures[hf.subplots[s].structureId]
        if ((st.rocketCells || []).includes(s) || used.has(s)) err(`Subplot ${s} already holds a rocket.`)
        used.add(s)
    }
    for (const d of drones) {
        const s = d?.launchS
        if (!assetCellOk(hf, s)) { err(`A recon plane can't launch from subplot ${s}.`); continue }
        const st = hf.structures[hf.subplots[s].structureId]
        if ((st.rocketCells || []).includes(s) || used.has(s)) err(`Subplot ${s} already hosts a rocket or a plane.`)
        used.add(s)
        if (d.targetS == null) err(`The recon plane from subplot ${s} isn't aimed.`)
        else if (battlefieldColumn(d.targetS) !== battlefieldColumn(s)) err(`The recon plane from subplot ${s} must aim inside its own corridor.`)
    }
    const spend = rockets.length * rules().rocketCost + drones.length * rules().droneCost
    if (spend > (player?.yield ?? 0)) err(`The plan spends ${spend} yield; ${player?.yield ?? 0} is available.`)

    return { ok: errors.length === 0, errors }
}
